import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Trophy, ChevronDown, ChevronRight, Award, Truck, CalendarClock,
  FileSpreadsheet, FileDown, Pencil, Plus, Save, X, Lock, Trash2, Gavel } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VendorQuoteDialog } from "@/components/VendorQuoteDialog";
import { toast } from "sonner";

const grab = (url, filename) => {
  const token = localStorage.getItem("matiqs_token");
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Could not build that export"));
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

const TERM_FIELDS = [
  ["quote_ref", "Quotation ref"],
  ["validity_days", "Validity (days)"],
  ["delivery_period", "Delivery period"],
  ["payment_terms", "Payment terms"],
  ["inclusions", "Inclusions"],
  ["exclusions", "Exclusions"],
  ["origin", "Country of origin"],
  ["warranty", "Warranty"],
  ["performance_security", "Performance security"],
  ["notes", "Notes"],
];

const CONTACT_FIELDS = [
  ["contact_person", "Contact person"],
  ["contact_email", "Email"],
  ["contact_phone", "Phone"],
];

// soft tint + header colour per vendor column block
const TINTS = ["rgba(29,79,216,0.07)", "rgba(180,83,9,0.08)", "rgba(15,118,110,0.08)",
               "rgba(109,40,217,0.08)", "rgba(190,18,60,0.07)", "rgba(77,124,15,0.09)"];
const HEADS = ["#1D4FD8", "#B45309", "#0F766E", "#6D28D9", "#BE123C", "#4D7C0F"];
const tintOf = (i) => TINTS[i % TINTS.length];
const headOf = (i) => HEADS[i % HEADS.length];
const DIVIDER = "border-l-[3px] border-slate-400/70 dark:border-slate-500/60";

export default function Comparison() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [detailed, setDetailed] = useState(false);
  const [awarding, setAwarding] = useState("");
  const [openQuote, setOpenQuote] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [terms, setTerms] = useState({});
  const [edits, setEdits] = useState({});
  const [rows, setRows] = useState([]);
  const [budget, setBudget] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/buyer/rfqs/${id}/comparison`);
      setData(data);
      const ov = data.overrides || {};
      setTerms(ov.terms || {});
      setEdits(ov.edits || {});
      setRows(ov.rows || []);
      setBudget(ov.budget || {});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load the comparison");
      navigate("/rfqs");
    }
  };
  useEffect(() => { load(); }, [id]);

  const award = async (quoteId, vendorName) => {
    if (!window.confirm(`Accept the quotation from ${vendorName}? They will be notified by email.`)) return;
    setAwarding(quoteId);
    try {
      await api.post(`/buyer/rfqs/${id}/award`, { quote_id: quoteId });
      toast.success(`${vendorName} awarded`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not award this quote");
    } finally {
      setAwarding("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/buyer/rfqs/${id}/comparison`, { terms, edits, rows, budget });
      toast.success("Comparison sheet saved");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save your changes");
    } finally {
      setSaving(false);
    }
  };

  const setTerm = (qid, key, value) =>
    setTerms((t) => ({ ...t, [qid]: { ...(t[qid] || {}), [key]: value } }));

  const setEdit = (itemId, key, value) =>
    setEdits((e) => ({ ...e, [itemId]: { ...(e[itemId] || {}), [key]: value } }));

  const setEditRate = (itemId, qid, value) =>
    setEdits((e) => ({
      ...e,
      [itemId]: { ...(e[itemId] || {}), rates: { ...((e[itemId] || {}).rates || {}), [qid]: num(value) } },
    }));

  const addRow = () =>
    setRows((r) => [...r, { id: `new-${Date.now()}`, description: "", unit: "", qty: 1, remarks: "", rates: {} }]);

  const setRow = (idx, key, value) =>
    setRows((r) => r.map((x, i) => (i === idx ? { ...x, [key]: value } : x)));

  const setRowRate = (idx, qid, value) =>
    setRows((r) => r.map((x, i) => (i === idx ? { ...x, rates: { ...(x.rates || {}), [qid]: num(value) } } : x)));

  const setBudgetRate = (itemId, value) =>
    setBudget((b) => ({ ...b, [itemId]: num(value) }));

  const boqLines = useMemo(
    () => (data?.lines || []).filter(
      (l) => !l.custom || l.vendor_addition || l.item_id === "vendor-additions"),
    [data]);
  const numbers = useMemo(() => {
    const m = {};
    let n = 0;
    for (const l of boqLines) if (l.kind === "item") m[l.item_id] = ++n;
    return m;
  }, [boqLines]);

  const lineQty = (l) => {
    const e = edits[l.item_id] || {};
    return e.qty !== undefined && e.qty !== "" ? num(e.qty) : num(l.qty);
  };
  const cellOf = (l, qid) => {
    const e = edits[l.item_id] || {};
    const ovRate = (e.rates || {})[qid];
    const c = l.cells?.[qid];
    if (ovRate === undefined && !c) return null;
    const rate = ovRate !== undefined ? num(ovRate) : num(c?.rate);
    const qty = e.qty !== undefined && e.qty !== "" ? num(e.qty) : num(c?.qty ?? l.qty);
    return {
      rate, qty, amount: rate * qty,
      unit: c?.unit || l.unit || "",
      remarks: c?.remarks || "",
      is_lowest: c?.is_lowest && ovRate === undefined,
      edited: ovRate !== undefined || c?.edited,
    };
  };

  const totals = useMemo(() => {
    const out = {};
    for (const s of data?.summary || []) {
      let sum = 0, touched = false;
      for (const l of boqLines) {
        if (l.kind !== "item") continue;
        const c = cellOf(l, s.quote_id);
        if (c) { sum += c.amount; touched = true; }
      }
      for (const r of rows) {
        const rate = (r.rates || {})[s.quote_id];
        if (rate !== undefined) { sum += num(rate) * num(r.qty); touched = true; }
      }
      out[s.quote_id] = touched
        ? Math.max(sum - num(s.discount), 0)
        : num(s.total);
    }
    return out;
  }, [data, edits, rows, boqLines]);

  const budgetRate = (l) => num(budget[l.item_id]);
  const budgetTotal = useMemo(() => {
    let sum = 0;
    for (const l of boqLines) {
      if (l.kind !== "item") continue;
      sum += num(budget[l.item_id]) * lineQty(l);
    }
    for (const r of rows) sum += num(r.budget) * num(r.qty);
    return sum;
  }, [budget, rows, edits, boqLines]);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const { rfq, summary, currency, can_edit } = data;
  const awarded = rfq.awarded_quote_id;
  const company = data.buyer_company || rfq.buyer_company || "";
  const vcols = detailed ? 5 : 1;
  const fixedCols = 8;

  return (
    <div data-testid="comparison-page" className="space-y-6">
      <button onClick={() => navigate(`/rfqs/${id}`)} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to RFQ
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {company && (
            <div data-testid="buyer-company" className="font-display text-base font-bold text-primary">{company}</div>
          )}
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold mt-1">Quote comparison</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">{rfq.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-mono">{rfq.rfq_ref}</span> · {summary.length} quote{summary.length === 1 ? "" : "s"} · {rfq.item_count} line items · prices in {currency}
          </p>
        </div>
        {summary.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-border bg-card p-0.5">
              {[["Compact", false], ["Detailed", true]].map(([label, val]) => (
                <button key={label} data-testid={`view-${label.toLowerCase()}`} onClick={() => setDetailed(val)}
                        className={`px-3.5 h-8 rounded-full text-xs font-bold transition-colors ${
                          detailed === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  {label}
                </button>
              ))}
            </div>
            {editing ? (
              <>
                <Button data-testid="save-sheet-button" onClick={save} disabled={saving} className="h-10 gap-2 font-semibold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save sheet
                </Button>
                <Button variant="ghost" data-testid="cancel-edit-button" onClick={() => { setEditing(false); load(); }} className="h-10 gap-2 font-semibold">
                  <X className="w-4 h-4" /> Cancel
                </Button>
              </>
            ) : (
              <Button variant="outline" data-testid="edit-sheet-button"
                      onClick={() => (can_edit ? setEditing(true) : navigate("/billing"))}
                      className="h-10 gap-2 font-semibold">
                {can_edit ? <Pencil className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Edit sheet
              </Button>
            )}
            <Button variant="outline" data-testid="export-excel-button"
                    onClick={() => grab(`/buyer/rfqs/${id}/comparison.xlsx`, `Comparison-${rfq.rfq_ref}.xlsx`)}
                    className="h-10 gap-2 font-semibold">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="export-pdf-button"
                    onClick={() => grab(`/buyer/rfqs/${id}/comparison.pdf`, `Comparison-${rfq.rfq_ref}.pdf`)}
                    className="h-10 gap-2 font-semibold">
              <FileDown className="w-4 h-4" /> PDF
            </Button>
          </div>
        )}
      </div>

      {!can_edit && summary.length > 0 && (
        <div data-testid="edit-upgrade-note" className="rounded-2xl border border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20 px-5 py-3.5 text-sm">
          <span className="font-semibold text-amber-900 dark:text-amber-200">A customisable comparison sheet is on Standard and Pro.</span>{" "}
          <span className="text-amber-800/90 dark:text-amber-200/80">Upgrade to adjust quantities and rates, add your own rows and rewrite the commercial terms.</span>{" "}
          <button onClick={() => navigate("/billing")} className="font-bold underline text-amber-900 dark:text-amber-200">See plans</button>
        </div>
      )}

      {data.vendor_limit?.hidden > 0 && (
        <div data-testid="vendor-limit-note" className="rounded-2xl border border-brand/40 bg-brand-soft/50 px-5 py-3.5 text-sm">
          <span className="font-semibold text-brand">
            Showing the {data.vendor_limit.max} best-priced quotes of {data.vendor_limit.total}.
          </span>{" "}
          <span className="text-muted-foreground">
            Your {data.plan_name} plan compares up to {data.vendor_limit.max} vendors per RFQ.
          </span>{" "}
          <button onClick={() => navigate("/billing")} className="font-bold underline text-brand">Compare them all</button>
        </div>
      )}

      {summary.length === 0 ? (
        <div data-testid="no-quotes" className="bg-card border border-border rounded-2xl p-12 text-center">
          <p className="font-semibold text-slate-900 dark:text-slate-100">No quotes yet</p>
          <p className="text-sm text-muted-foreground mt-1">They will appear here the moment a vendor submits, or as soon as you add an emailed reply.</p>
        </div>
      ) : (
        <>
          <div data-testid="comparison-summary" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {summary.map((s, i) => (
              <div
                key={s.quote_id}
                data-testid={`summary-card-${i}`}
                className={`rounded-2xl border p-5 transition-all ${
                  awarded === s.quote_id
                    ? "border-emerald-500/60 bg-emerald-50/60 dark:bg-emerald-950/30"
                    : s.is_lowest ? "border-brand/50 bg-brand-soft/40" : "border-border bg-card"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{s.vendor_name}</div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mt-0.5">
                      {s.source === "matiqs" ? "Via Matiqs"
                        : s.source === "excel_ai" ? "Excel read by AI"
                        : s.source === "pdf_ai" ? "PDF read by AI" : "Entered manually"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                  {awarded === s.quote_id && (
                    <span data-testid={`awarded-badge-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider shrink-0">
                      <Award className="w-3 h-3" /> Awarded
                    </span>
                  )}
                  {s.is_lowest && (
                    <span data-testid={`lowest-badge-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand text-brand-foreground text-[10px] font-bold uppercase tracking-wider shrink-0">
                      <Trophy className="w-3 h-3" /> Lowest
                    </span>
                  )}
                  </div>
                </div>

                <div className="mt-4 font-mono text-2xl font-extrabold tabular-nums text-slate-900 dark:text-white">
                  {currency} {money(totals[s.quote_id])}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Subtotal {money(s.subtotal)}{s.discount > 0 ? ` · less ${money(s.discount)} discount` : ""}
                </div>

                <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {s.delivery_days > 0 && <span className="inline-flex items-center gap-1"><Truck className="w-3 h-3" />{s.delivery_days} days</span>}
                  <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" />{s.validity_days} days valid</span>
                  {s.priced_lines > 0 && <span>{s.priced_lines}/{rfq.item_count} lines priced</span>}
                </div>
                {s.notes && <p className="text-xs text-muted-foreground mt-3 italic">“{s.notes}”</p>}

                {!awarded && (
                  <div className="mt-5 space-y-2">
                    <Button
                      data-testid={`award-button-${i}`}
                      onClick={() => award(s.quote_id, s.vendor_name)}
                      disabled={awarding === s.quote_id}
                      variant={s.is_lowest ? "default" : "outline"}
                      className="w-full h-10 font-semibold gap-2"
                    >
                      {awarding === s.quote_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                      Accept this quote
                    </Button>
                    <button data-testid={`open-quote-${i}`} onClick={() => setOpenQuote(s.quote_id)}
                            className="w-full text-xs font-bold text-primary hover:underline inline-flex items-center justify-center gap-1.5">
                      <Gavel className="w-3.5 h-3.5" /> Open quotation · ask best & final
                    </button>
                  </div>
                )}
                {awarded && (
                  <button data-testid={`open-quote-${i}`} onClick={() => setOpenQuote(s.quote_id)}
                          className="w-full mt-5 text-xs font-bold text-primary hover:underline">
                    Open this quotation
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* ---------- quotation summary / commercial terms ---------- */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <div className="font-semibold text-slate-900 dark:text-slate-100">Quotation summary</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Commercial terms as quoted by each vendor{editing ? " — edit anything that needs levelling before you export" : ""}. Edits stay on this comparison and never change the RFQ.
              </div>
            </div>
            <div className="overflow-x-auto">
              <table data-testid="terms-table" className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider bg-slate-900 text-white">
                    <th className="text-left font-bold px-4 py-3 min-w-[180px]">Item</th>
                    {summary.map((s, i) => (
                      <th key={s.quote_id} style={{ backgroundColor: headOf(i) }}
                          className={`text-left font-bold px-4 py-3 min-w-[200px] text-white ${DIVIDER}`}>
                        {s.vendor_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border bg-secondary/30 font-bold">
                    <td className="px-4 py-3">Quoted total ({currency})</td>
                    {summary.map((s, i) => (
                      <td key={s.quote_id} data-testid={`terms-total-${s.quote_id}`}
                          style={{ backgroundColor: tintOf(i) }}
                          className={`px-4 py-3 font-mono tabular-nums ${DIVIDER}`}>
                        {money(totals[s.quote_id])}
                      </td>
                    ))}
                  </tr>
                  {CONTACT_FIELDS.map(([key, label]) => (
                    <tr key={key} className="border-t border-border">
                      <td className="px-4 py-2.5 text-muted-foreground font-medium">{label}</td>
                      {summary.map((s, i) => (
                        <td key={s.quote_id} style={{ backgroundColor: tintOf(i) }}
                            className={`px-4 py-2.5 align-top ${DIVIDER}`}>
                          <span data-testid={`contact-${key}-${s.quote_id}`} className="text-slate-800 dark:text-slate-200">
                            {s[key] || <span className="text-muted-foreground">—</span>}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {TERM_FIELDS.map(([key, label]) => (
                    <tr key={key} className="border-t border-border">
                      <td className="px-4 py-2.5 text-muted-foreground font-medium">{label}</td>
                      {summary.map((s, i) => {
                        const val = (terms[s.quote_id] || {})[key] ?? (s.terms || {})[key] ?? "";
                        return (
                          <td key={s.quote_id} style={{ backgroundColor: tintOf(i) }}
                              className={`px-4 py-2 align-top ${DIVIDER}`}>
                            {editing ? (
                              <Input data-testid={`term-${key}-${s.quote_id}`} value={val}
                                     onChange={(e) => setTerm(s.quote_id, key, e.target.value)}
                                     className="h-9 text-sm bg-card" />
                            ) : (
                              <span data-testid={`term-view-${key}-${s.quote_id}`} className="text-slate-800 dark:text-slate-200">
                                {String(val || "") || <span className="text-muted-foreground">—</span>}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---------- line grid ---------- */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <button
              data-testid="toggle-line-detail"
              onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between gap-3 px-6 py-4 border-b border-border hover:bg-secondary/50 transition-colors"
            >
              <div className="text-left">
                <div className="font-semibold text-slate-900 dark:text-slate-100">Line-by-line detail</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {data.has_line_detail
                    ? `Cheapest rate per line is highlighted. ${detailed ? "Showing rate, quantity, unit, amount and remarks per vendor." : "Showing rates only — switch to Detailed for quantities, amounts and remarks."}`
                    : "No vendor has submitted line-level rates yet — totals only."}
                </div>
              </div>
              {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
            </button>

            {open && (
              <div className="overflow-x-auto">
                <table data-testid="line-detail-table" className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider bg-slate-900 text-white">
                      <th colSpan={fixedCols} className="text-left font-bold px-4 py-2">Contractor BOQ</th>
                      {summary.map((s, i) => (
                        <th key={s.quote_id} colSpan={vcols} style={{ backgroundColor: headOf(i) }}
                            className={`text-center font-bold px-4 py-2 whitespace-nowrap text-white ${DIVIDER}`}>
                          {s.vendor_name}
                        </th>
                      ))}
                    </tr>
                    <tr className="text-[11px] uppercase tracking-wider text-white bg-slate-800">
                      <th className="text-left font-bold px-4 py-3 w-8">#</th>
                      <th className="text-left font-bold px-4 py-3 min-w-[240px]">Description</th>
                      <th className="text-left font-bold px-3 py-3 whitespace-nowrap">Ref</th>
                      <th className="text-right font-bold px-3 py-3 whitespace-nowrap">Qty</th>
                      <th className="text-left font-bold px-3 py-3 whitespace-nowrap">Unit</th>
                      <th className="text-right font-bold px-3 py-3 whitespace-nowrap">Rate</th>
                      <th className="text-right font-bold px-3 py-3 whitespace-nowrap">Amount</th>
                      <th className="text-left font-bold px-3 py-3 min-w-[140px]">Remarks</th>
                      {summary.map((s, i) => (
                        <React.Fragment key={s.quote_id}>
                          <th className={`text-right font-bold px-3 py-3 whitespace-nowrap ${DIVIDER}`}>Rate</th>
                          {detailed && <th className="text-right font-bold px-3 py-3 whitespace-nowrap">Qty</th>}
                          {detailed && <th className="text-left font-bold px-3 py-3 whitespace-nowrap">Unit</th>}
                          {detailed && <th className="text-right font-bold px-3 py-3 whitespace-nowrap">Amount</th>}
                          {detailed && <th className="text-left font-bold px-3 py-3 min-w-[130px]">Remarks</th>}
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {boqLines.map((l, i) => {
                      if (l.kind !== "item") {
                        return (
                          <tr key={l.item_id} data-testid={`compare-header-${i}`} className="border-t border-border bg-secondary/40">
                            <td className="px-4 py-2 text-xs text-muted-foreground font-mono" />
                            <td
                              colSpan={fixedCols - 1 + vcols * summary.length}
                              className={`px-4 py-2 font-bold text-slate-900 dark:text-slate-100 ${l.kind === "subtotal" ? "text-right font-mono" : ""}`}
                              style={{ paddingLeft: `${16 + (l.level || 0) * 16}px` }}
                            >
                              {l.description}
                            </td>
                          </tr>
                        );
                      }
                      const q = lineQty(l);
                      return (
                      <tr key={l.item_id} data-testid={`compare-line-${i}`} className="border-t border-border">
                        <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">{numbers[l.item_id]}</td>
                        <td className="px-4 py-2.5" style={{ paddingLeft: `${16 + (l.level || 0) * 16}px` }}>{l.description}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">{l.serial || l.page || l.sheet || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-muted-foreground whitespace-nowrap">
                          {editing ? (
                            <Input data-testid={`qty-input-${l.item_id}`} value={q}
                                   onChange={(e) => setEdit(l.item_id, "qty", e.target.value)}
                                   className="h-9 w-20 text-right text-sm" />
                          ) : money(q)}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{l.unit || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap">
                          {editing ? (
                            <Input data-testid={`budget-rate-${l.item_id}`} value={budget[l.item_id] ?? ""}
                                   placeholder="0.00"
                                   onChange={(e) => setBudgetRate(l.item_id, e.target.value)}
                                   className="h-9 w-24 text-right text-sm bg-card" />
                          ) : (budgetRate(l) > 0 ? money(budgetRate(l)) : <span className="text-muted-foreground">—</span>)}
                        </td>
                        <td data-testid={`budget-amount-${l.item_id}`} className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap font-semibold">
                          {budgetRate(l) > 0 ? money(budgetRate(l) * q) : <span className="text-muted-foreground font-normal">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">
                          {editing ? (
                            <Input data-testid={`remarks-input-${l.item_id}`}
                                   value={(edits[l.item_id] || {}).remarks ?? l.remarks ?? ""}
                                   onChange={(e) => setEdit(l.item_id, "remarks", e.target.value)}
                                   className="h-9 text-sm" />
                          ) : (l.remarks || "—")}
                        </td>
                        {summary.map((s, vi) => {
                          const c = cellOf(l, s.quote_id);
                          const tint = { backgroundColor: tintOf(vi) };
                          return (
                            <React.Fragment key={s.quote_id}>
                              <td
                                data-testid={`cell-${i}-${s.quote_id}`}
                                style={c?.is_lowest ? undefined : tint}
                                className={`px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap ${DIVIDER} ${
                                  c?.is_lowest ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 font-bold" : ""
                                }`}
                              >
                                {editing ? (
                                  <Input data-testid={`rate-input-${l.item_id}-${s.quote_id}`}
                                         value={c ? c.rate : ""}
                                         onChange={(e) => setEditRate(l.item_id, s.quote_id, e.target.value)}
                                         className="h-9 w-24 text-right text-sm bg-card" />
                                ) : c ? money(c.rate) : <span className="text-muted-foreground">—</span>}
                              </td>
                              {detailed && <td style={tint} className="px-3 py-2.5 text-right font-mono text-muted-foreground whitespace-nowrap">{c ? money(c.qty) : "—"}</td>}
                              {detailed && <td style={tint} className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{c ? (c.unit || "—") : "—"}</td>}
                              {detailed && (
                                <td data-testid={`amount-${i}-${s.quote_id}`} style={tint} className="px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap font-semibold">
                                  {c ? money(c.amount) : "—"}
                                </td>
                              )}
                              {detailed && <td style={tint} className="px-3 py-2.5 text-xs text-muted-foreground">{c?.remarks || "—"}</td>}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                      );
                    })}

                    {rows.map((r, idx) => (
                      <tr key={r.id} data-testid={`custom-row-${idx}`} className="border-t border-border bg-primary/5">
                        <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">+</td>
                        <td className="px-4 py-2.5">
                          {editing ? (
                            <Input data-testid={`custom-desc-${idx}`} value={r.description} placeholder="Added line"
                                   onChange={(e) => setRow(idx, "description", e.target.value)} className="h-9 text-sm" />
                          ) : <span className="italic">{r.description || "Added line"}</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">added</td>
                        <td className="px-3 py-2.5 text-right font-mono">
                          {editing ? (
                            <Input data-testid={`custom-qty-${idx}`} value={r.qty}
                                   onChange={(e) => setRow(idx, "qty", e.target.value)} className="h-9 w-20 text-right text-sm" />
                          ) : money(r.qty)}
                        </td>
                        <td className="px-3 py-2.5">
                          {editing ? (
                            <Input data-testid={`custom-unit-${idx}`} value={r.unit}
                                   onChange={(e) => setRow(idx, "unit", e.target.value)} className="h-9 w-20 text-sm" />
                          ) : (r.unit || "—")}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono">
                          {editing ? (
                            <Input data-testid={`custom-budget-rate-${idx}`} value={r.budget ?? ""} placeholder="0.00"
                                   onChange={(e) => setRow(idx, "budget", num(e.target.value))} className="h-9 w-24 text-right text-sm bg-card" />
                          ) : (num(r.budget) > 0 ? money(r.budget) : "—")}
                        </td>
                        <td data-testid={`custom-budget-amount-${idx}`} className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold">
                          {num(r.budget) > 0 ? money(num(r.budget) * num(r.qty)) : <span className="text-muted-foreground font-normal">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          {editing ? (
                            <div className="flex items-center gap-1">
                              <Input data-testid={`custom-remarks-${idx}`} value={r.remarks}
                                     onChange={(e) => setRow(idx, "remarks", e.target.value)} className="h-9 text-sm" />
                              <button data-testid={`custom-delete-${idx}`} onClick={() => setRows((x) => x.filter((_, i2) => i2 !== idx))}
                                      className="text-muted-foreground hover:text-destructive p-1">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (r.remarks || "—")}
                        </td>
                        {summary.map((s, vi) => {
                          const rate = (r.rates || {})[s.quote_id];
                          const tint = { backgroundColor: tintOf(vi) };
                          return (
                            <React.Fragment key={s.quote_id}>
                              <td style={tint} className={`px-3 py-2.5 text-right font-mono ${DIVIDER}`}>
                                {editing ? (
                                  <Input data-testid={`custom-rate-${idx}-${s.quote_id}`} value={rate ?? ""}
                                         onChange={(e) => setRowRate(idx, s.quote_id, e.target.value)}
                                         className="h-9 w-24 text-right text-sm bg-card" />
                                ) : rate !== undefined ? money(rate) : "—"}
                              </td>
                              {detailed && <td style={tint} className="px-3 py-2.5 text-right font-mono text-muted-foreground">{money(r.qty)}</td>}
                              {detailed && <td style={tint} className="px-3 py-2.5 text-muted-foreground">{r.unit || "—"}</td>}
                              {detailed && <td style={tint} className="px-3 py-2.5 text-right font-mono font-semibold">{rate !== undefined ? money(num(rate) * num(r.qty)) : "—"}</td>}
                              {detailed && <td style={tint} className="px-3 py-2.5" />}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}

                    {editing && (
                      <tr className="border-t border-border">
                        <td colSpan={fixedCols + vcols * summary.length} className="px-4 py-3">
                          <Button variant="outline" size="sm" data-testid="add-row-button" onClick={addRow} className="gap-2 font-semibold">
                            <Plus className="w-4 h-4" /> Add a line
                          </Button>
                        </td>
                      </tr>
                    )}

                    <tr className="border-t-2 border-border bg-secondary/40 font-bold">
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3">Total</td>
                      <td colSpan={3} className="px-4 py-3" />
                      <td data-testid="budget-total" className="px-3 py-3 text-right font-mono tabular-nums whitespace-nowrap">
                        {budgetTotal > 0 ? money(budgetTotal) : ""}
                      </td>
                      <td className="px-4 py-3" />
                      {summary.map((s, vi) => (
                        <React.Fragment key={s.quote_id}>
                          {detailed ? (
                            <>
                              <td colSpan={3} style={{ backgroundColor: tintOf(vi) }} className={`px-3 py-3 ${DIVIDER}`} />
                              <td data-testid={`total-${s.quote_id}`} style={{ backgroundColor: tintOf(vi) }}
                                  className="px-3 py-3 text-right font-mono tabular-nums whitespace-nowrap">
                                {money(totals[s.quote_id])}
                              </td>
                              <td style={{ backgroundColor: tintOf(vi) }} className="px-3 py-3" />
                            </>
                          ) : (
                            <td data-testid={`total-${s.quote_id}`} style={{ backgroundColor: tintOf(vi) }}
                                className={`px-3 py-3 text-right font-mono tabular-nums whitespace-nowrap ${DIVIDER}`}>
                              {money(totals[s.quote_id])}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {openQuote && (
        <VendorQuoteDialog rfqId={id} quoteId={openQuote}
                           onClose={() => setOpenQuote("")} onChanged={load} />
      )}
    </div>
  );
}
